import { vars } from '@/app/config/variables';
import { Form } from '@/shared/form/form';
import { classInstance, debounce } from '@/shared/helpers/helpers';
import { renderSearchResults } from './render-search-results';
import { renderPopularSearches } from './render-popular-searches';
import { renderSearchCategories } from './render-search-categories';

export class Search {
	static modalListenersAttached = false;

	constructor(selector, options = {}, callbacks = {}) {
		this.$container = selector;
		this.minQueryLength = options.minQueryLength || 3;
		this.dataUrl = options.dataUrl || '../data/search/search-results.json';
		this.debounceDelay = options.debounceDelay || 1000;
		this.requestMode = options.requestMode || 'auto';
		this.postHeaders = options.postHeaders;
		this.postBody = options.postBody;
		this.callbacks = callbacks;

		if (!this.$container) return;

		this.$container.search = this;
		classInstance.set(this.$container, { search: this });
		this.laptopMQ = window.matchMedia(`(min-width: ${vars.breakpoint.laptop}px)`);
		this.setupRunSearch();

		this.selectorField = options.selectorField || '.js-search__field';
		this.selectorClearButton = options.selectorClearButton || '.js-search__clear-button';
		this.selectorOpenButton = options.selectorOpenButton || '.js-search-open-button';
		this.selectorModal = options.selectorModal || '#modal-search';
		this.selectorModalLayer = options.selectorModalLayer || '.js-modal-layer';
		this.selectorResult = options.selectorResult || '.js-search-result';
		this.classResultLoaded = options.classResultLoaded || 'search-result--loaded';
		this.selectorResultItems = options.selectorResultItems || '.js-search-result__items';
		this.selectorResultMessage = options.selectorResultMessage || '.js-search-result__message';
		this.selectorResultAsideItem = options.selectorResultAsideItem || '.search-result-aside__item';
		this.selectorClearHistoryButton = options.selectorClearHistoryButton || '.js-search-clear-history-button';
		this.selectorRemoveHistoryItemButton = options.selectorRemoveHistoryItemButton || '.js-search-remove-history-item-button';
		this.selectorPopularSearchesContainer = options.selectorPopularSearchesContainer || '.js-search-result__popular-searches';
		this.selectorFoundInCategoriesContainer = options.selectorFoundInCategoriesContainer || '.js-search-result__found-in-categories';
		this.selectorShowAllLink = options.selectorShowAllLink || '.js-search-result__show-all';

		this.init();
	}

	init() {
		this.$field = this.$container.querySelector(this.selectorField);
		this.$clearButton = this.$container.querySelector(this.selectorClearButton);
		this.$openButtons = document.querySelectorAll(this.selectorOpenButton);
		this.$modal = document.querySelector(this.selectorModal);
		if (!this.$modal) return;

		this.$modalLayer = document.querySelector(this.selectorModalLayer);
		this.$result = this.$modal.querySelector(this.selectorResult);
		this.$resultItems = this.$modal.querySelector(this.selectorResultItems);
		this.$resultMessage = this.$modal.querySelector(this.selectorResultMessage);
		this.$clearHistoryButton = this.$modal.querySelector(this.selectorClearHistoryButton);
		this.$removeHistoryItemButtons = this.$modal.querySelectorAll(this.selectorRemoveHistoryItemButton);
		this.$showAllLink = this.$modal.querySelector(this.selectorShowAllLink);
		this.$popularSearchesContainer = this.$modal.querySelector(this.selectorPopularSearchesContainer);
		this.$foundInCategoriesContainer = this.$modal.querySelector(this.selectorFoundInCategoriesContainer);
		const formCallbacks = { onSubmit: this.callbacks.onSubmit };
		let formInstance = classInstance.get(this.$container)?.form;
		if (!formInstance) {
			formInstance = new Form(this.$container, formCallbacks);
		} else {
			formInstance.addCallbacks(formCallbacks);
		}
		this.form = formInstance;

		this.$field.addEventListener('focus', this.handleFieldFocus);
		this.$field.addEventListener('input', this.handleFieldInput);
		this.$clearButton.addEventListener('click', this.handleClearButtonClick);
		this.$openButtons.forEach((button) => {
			button.addEventListener('click', this.handleOpenButtonClick);
		});
		this.laptopMQ.addEventListener('change', this.closeSearch);
		this.$field.value = '';

		this.attachModalListeners();
	}

	addOptions = (options) => {
		this.options = { ...this.options, ...options };

		if (options.requestMode) this.requestMode = options.requestMode;
		if (options.dataUrl) this.dataUrl = options.dataUrl;
		if (options.minQueryLength) this.minQueryLength = options.minQueryLength;
		if (options.debounceDelay) {
			this.debounceDelay = options.debounceDelay;
			this.setupRunSearch();
		}
		if (options.postHeaders) this.postHeaders = options.postHeaders;
		if (options.postBody) this.postBody = options.postBody;
	};

	showSearch = () => {
		if (this.$modal.classList.contains('modal-window--active')) return;
		this.$modalLayer.classList.add('modal-layer--search');
		window.app.modal.open(this.selectorModal);
		if (!this.laptopMQ.matches) {
			setTimeout(() => {
				this.$modal.querySelector(this.selectorField)?.focus();
			}, 100);
		}
	};

	closeSearch = () => {
		window.app.modal.close();
		this.$field.blur();
		this.$field.value = '';
	};

	handleFieldFocus = () => {
		this.showSearch();
	};

	handleFieldInput = () => {
		this.showSearch();
		const value = this.$field.value;

		if (this.callbacks?.onInput) {
			this.callbacks.onInput(value);
		}
	};

	handleClearButtonClick = () => {
		if (this.laptopMQ.matches) {
			window.app.modal.close();
		} else {
			this.$field.focus();
		}
		this.$result.classList.remove(this.classResultLoaded);
	};

	handleOpenButtonClick = (e) => {
		e.preventDefault();
		this.showSearch();
	};

	handleModalClose = () => {
		this.$modalLayer.classList.add('modal-layer--search--closing');
		setTimeout(() => {
			this.$modalLayer.classList.remove('modal-layer--search', 'modal-layer--search--closing');
		}, 400);
	};

	attachModalListeners = () => {
		if (Search.modalListenersAttached) return;

		this.$modal.addEventListener('modalClose', this.handleModalClose);
		this.$clearHistoryButton?.addEventListener('click', this.handleClearHistoryButtonClick);
		this.$removeHistoryItemButtons.forEach((button) => {
			button.addEventListener('click', this.handleRemoveHistoryItemButtonClick);
		});

		Search.modalListenersAttached = true;
	};

	handleClearHistoryButtonClick = () => {
		this.$clearHistoryButton?.dispatchEvent(
			new CustomEvent('search:clearSearchHistory', {
				bubbles: true,
			}),
		);
	};

	handleRemoveHistoryItemButtonClick = ({ currentTarget }) => {
		const button = currentTarget;
		const item = button.closest(this.selectorResultAsideItem);

		button.dispatchEvent(
			new CustomEvent('search:removeSearchHistoryItem', {
				bubbles: true,
				detail: { item },
			}),
		);
	};

	addCallbacks = (callbacks) => {
		this.callbacks = { ...this.callbacks, ...callbacks };
		if (callbacks.onSubmit) {
			this.form.addCallbacks({ onSubmit: callbacks.onSubmit });
		}
	};

	renderResults = (results) => {
		const container = this.$resultItems;
		if (!container) return;

		this.$result.classList.add(this.classResultLoaded);
		this.hideMessage();
		renderSearchResults(container, results);
	};

	renderPopularSearchItems = (results) => {
		const container = this.$popularSearchesContainer;
		if (!container) return;

		renderPopularSearches(container, results);
	};

	renderFoundInCategories = (results) => {
		const container = this.$foundInCategoriesContainer;
		if (!container) return;

		renderSearchCategories(container, results);
	};

	getRequestMode(endpoint) {
		if (this.requestMode !== 'auto') {
			return this.requestMode;
		}

		if (endpoint.endsWith('.json')) {
			return 'get';
		}

		return 'post';
	}

	fetchResults = async (query, endpoint = this.dataUrl) => {
		if (this.abortController) {
			this.abortController.abort();
		}

		this.abortController = new AbortController();
		const { signal } = this.abortController;

		const mode = this.getRequestMode(endpoint);

		try {
			let response;

			if (mode === 'get') {
				const url = `${endpoint}?q=${encodeURIComponent(query)}`;
				response = await fetch(url, { signal });
			} else {
				const defaultHeaders = {
					'Accept': 'application/json',
					'Content-Type': 'application/json',
					'X-Requested-With': 'XMLHttpRequest',
				};

				response = await fetch(endpoint, {
					method: 'POST',
					headers: this.postHeaders !== undefined ? this.postHeaders : defaultHeaders,
					body: JSON.stringify({
						...(this.postBody ?? {}),
						q: query.trim(),
						l: this.minQueryLength,
						ajax_call: 'y',
					}),
					signal,
				});
			}

			if (!response.ok) throw new Error(`HTTP ${response.status}`);

			const result = await response.json();

			if (!result?.product?.length) {
				this.showMessage('Поиск по Вашему запросу не дал результатов');
				return;
			}

			this.renderResults(result);
			this.renderPopularSearchItems(result);
			this.renderFoundInCategories(result);
			this.$showAllLink.href = result.page || '#!';
		} catch (error) {
			if (error.name === 'AbortError') return;

			console.error('Search request failed:', error);

			this.renderResults({ product: [] });
			this.renderPopularSearchItems({ request: [] });
			this.renderFoundInCategories({ category: [] });
			this.$showAllLink.href = '#!';
		} finally {
			this.abortController = null;
		}
	};

	setupRunSearch() {
		let firstCall = true;

		const debouncedSearch = debounce((query, dataUrl) => this.fetchResults(query, dataUrl), this.debounceDelay);

		this.runSearch = (query, dataUrl = this.dataUrl) => {
			const trimmedQuery = query.trim();

			if (trimmedQuery.length === 0 && !firstCall) {
				if (this.abortController) this.abortController.abort();
				debouncedSearch.cancel();
				this.renderResults({ product: [] });
				this.renderFoundInCategories({ category: [] });
				this.$result.classList.remove(this.classResultLoaded);
				return;
			}

			if (trimmedQuery.length < this.minQueryLength) {
				debouncedSearch.cancel();
				return;
			}

			if (firstCall) {
				this.fetchResults(query, dataUrl);
				firstCall = false;
			} else {
				debouncedSearch(query, dataUrl);
			}
		};
	}

	showMessage = (message) => {
		this.$result.classList.remove(this.classResultLoaded);
		this.$resultMessage.textContent = message;
	};

	hideMessage = () => {
		this.$resultMessage.textContent = '';
	};
}
